import { getIdToken } from "../auth";
import { getPresignedBlobBaseUrl } from "./waveBlobDownload";

export type BlobAttachmentKind = "image" | "pdf" | "file" | "video";

export type BlobUploadResult = {
  s3Key: string;
  publicURL: string;
};

/** Infer blob kind for presigned upload (aligned with pdf-presigned Lambda). */
export function inferBlobKind(file: File): BlobAttachmentKind {
  const mime = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|heic)$/.test(name)) {
    return "image";
  }
  if (mime === "application/pdf" || name.endsWith(".pdf")) return "pdf";
  if (mime.startsWith("video/") || /\.(mp4|mov|webm)$/.test(name)) return "video";
  return "file";
}

export function normalizedContentType(file: File, kind: BlobAttachmentKind): string {
  if (file.type.trim()) return file.type;
  switch (kind) {
    case "image":
      return "image/jpeg";
    case "pdf":
      return "application/pdf";
    case "video":
      return "video/mp4";
    default:
      return "application/octet-stream";
  }
}

async function requestUploadURL(
  kind: BlobAttachmentKind,
  filename: string,
  contentLength: number,
  contentType: string,
  ownerId: string,
): Promise<{ url: string; s3Key: string; publicURL: string }> {
  const base = getPresignedBlobBaseUrl();
  if (!base) throw new Error("Storage cloud non configurato.");

  const token = await getIdToken();
  let res: Response;
  try {
    res = await fetch(`${base}/blob/upload-url`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        kind,
        filename,
        contentLength,
        contentType,
        ownerId,
      }),
    });
  } catch (e) {
    if (e instanceof TypeError) {
      throw new Error(
        "Impossibile contattare lo storage cloud (rete o CORS). Riprova tra poco.",
      );
    }
    throw e;
  }
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`Upload URL (${res.status}): ${text.slice(0, 200)}`);
  }
  const json = JSON.parse(text) as { url?: string; s3Key?: string; publicURL?: string };
  if (!json.url || !json.s3Key) throw new Error("Risposta upload non valida.");
  return {
    url: json.url,
    s3Key: json.s3Key,
    publicURL: json.publicURL ?? "",
  };
}

export type WaveAttachmentRegisterFields = {
  spaceId?: string;
  blockId?: string;
  mimeType?: string;
};

/** Upload a browser File to S3 via presigned URL. */
export async function uploadBlobFile(
  file: File,
  ownerId = "wave",
  register?: { waveId: string } & WaveAttachmentRegisterFields,
): Promise<BlobUploadResult> {
  const kind = inferBlobKind(file);
  const contentType = normalizedContentType(file, kind);
  const presigned = await requestUploadURL(
    kind,
    file.name,
    file.size,
    contentType,
    ownerId,
  );
  if (register?.waveId) {
    await registerWaveBlobAttachment(register.waveId, presigned.s3Key, file.name, {
      spaceId: register.spaceId,
      blockId: register.blockId,
      mimeType: contentType,
    });
  }
  const putRes = await fetch(presigned.url, {
    method: "PUT",
    headers: {
      "Content-Type": contentType,
      "Content-Length": String(file.size),
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Upload blob (${putRes.status})`);
  }
  return { s3Key: presigned.s3Key, publicURL: presigned.publicURL };
}

/** Register uploaded attachment for cross-participant download (pdf-presigned API). */
export async function registerWaveBlobAttachment(
  waveId: string,
  s3Key: string,
  fileName: string,
  extra?: WaveAttachmentRegisterFields,
): Promise<void> {
  const base = getPresignedBlobBaseUrl();
  if (!base) return;
  const token = await getIdToken();
  const body: Record<string, string> = { waveId, s3Key, fileName };
  if (extra?.spaceId) body.spaceId = extra.spaceId;
  if (extra?.blockId) body.blockId = extra.blockId;
  if (extra?.mimeType) body.mimeType = extra.mimeType;
  const res = await fetch(`${base}/blob/register-wave-attachment`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`Register attachment (${res.status}): ${text.slice(0, 200)}`);
  }
}

/** Request presigned GET URL for download/open. */
export async function signedDownloadURL(
  s3Key: string,
  waveId?: string,
  inline = false,
  fileName?: string,
): Promise<string> {
  const base = getPresignedBlobBaseUrl();
  if (!base) throw new Error("Storage cloud non configurato.");
  const token = await getIdToken();
  const isSharedCover = s3Key.startsWith("covers/");
  const path = isSharedCover ? "/pdf/download-url" : "/blob/download-url";
  const body: Record<string, string | boolean> = { s3Key };
  if (!isSharedCover && waveId?.trim()) body.waveId = waveId.trim();
  if (inline) body.inline = true;
  if (fileName?.trim()) body.filename = fileName.trim();
  let res: Response;
  try {
    res = await fetch(`${base}${path}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    if (e instanceof TypeError) {
      throw new Error(
        "Impossibile contattare lo storage cloud (rete o CORS). Riprova o usa Scarica.",
      );
    }
    throw e;
  }
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`Download URL (${res.status}): ${text.slice(0, 200)}`);
  }
  const json = JSON.parse(text) as { url?: string };
  if (!json.url) throw new Error("Risposta download non valida.");
  return json.url;
}

export async function openBlobDownload(s3Key: string, fileName?: string, waveId?: string): Promise<void> {
  const url = await signedDownloadURL(s3Key, waveId);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  if (fileName) a.download = fileName;
  a.click();
}

/** Open attachment in a new browser tab (preview when supported). */
export async function openBlobInNewTab(s3Key: string, fileName?: string, waveId?: string): Promise<void> {
  const url = await signedDownloadURL(s3Key, waveId, true, fileName);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
